import { LicenseNextAction, LicenseOutcome, LicenseType } from '../../models/license';

/**
 * Every customer-facing string in the license modal (MDG-359). Copy branches
 * on signals the backend sets (outcome code + data), never on the parser's
 * free-text reason. Benev ("relaxed" tenants) differ: state-only address match,
 * licenses that belong to a medical director, board verification sheets.
 */

export const WAIT_COPY = 'Please allow up to 24 hours for processing. No follow-up is needed during this time. We\'ll email you once the review is complete.';

/** "Oct 5, 4:14 PM CDT": upload times are shown in the viewer's own time zone, named. */
export function formatUploadTime(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
}

export const LABEL: Record<LicenseType, string> = {
  state_license: 'state license',
  dea_license: 'DEA registration',
};

export const TITLE: Record<LicenseType, string> = {
  state_license: 'State license',
  dea_license: 'DEA registration',
};

export interface CopyContext {
  readonly licenseType: LicenseType;
  /** Shared licenses (incl. a medical director's) and board records accepted. */
  readonly relaxedTenant: boolean;
  /** The license address need only match the facility's state. */
  readonly stateOnlyAddress: boolean;
  readonly onFileNumber: string | null;
  readonly facilityState: string | null;
}

export function guidance(ctx: CopyContext): readonly string[] {
  if (ctx.licenseType === 'dea_license') {
    return [
      'Upload a clear photo of the DEA registration certificate, or a file of it (PDF, JPEG or PNG).',
      ctx.onFileNumber ? `The DEA number must match ${ctx.onFileNumber}.` : 'The DEA number on the certificate must be readable.',
      'The registration must not be expired.',
    ];
  }
  const numberRule = ctx.onFileNumber
    ? `The license number must match ${ctx.onFileNumber}.`
    : 'The license number must be readable.';
  return [
    'Upload a clear photo of the state license itself (the certificate or wallet card), or a file of it (PDF, JPEG or PNG).',
    numberRule,
    ctx.stateOnlyAddress
      ? `The license must be issued in the facility's state${ctx.facilityState ? ` (${ctx.facilityState})` : ''}.`
      : 'The address on the license must match the facility\'s delivery address.',
    ...(ctx.relaxedTenant ? ['It can be the license of your medical director.'] : []),
  ];
}

export const BOARD_SHEET_STEPS: readonly string[] = [
  'Search online for your state\'s license verification (for example "Florida license verification").',
  'Look up the license by number or name and open its record.',
  'Save the page as a PDF (Print → Save as PDF) or take a screenshot.',
  'Choose "Upload a different file" and upload that file.',
];

export interface OutcomeCopy {
  readonly tone: 'success' | 'warning' | 'info';
  readonly title: string;
  readonly body: readonly string[];
  /** Show the state board record note: the relaxed tenant accepts that record
   *  when the license itself won't verify (rejected elsewhere by agreement). */
  readonly boardHint?: boolean;
}

export function outcomeCopy(outcome: LicenseOutcome, ctx: CopyContext, facilityVetted: boolean): OutcomeCopy {
  const d = outcome.data;
  const label = LABEL[ctx.licenseType];
  const boardHint = ctx.relaxedTenant && ctx.licenseType === 'state_license';
  switch (outcome.code) {
    case 'verified':
      return {
        tone: 'success',
        title: 'License verified',
        body: [
          `Your ${label} is active on your account through ${formatDate(d.expiry)}.`,
          facilityVetted ? 'You can place orders now.' : 'Your account review is in progress — we\'ll email you when it\'s complete.',
        ],
      };
    case 'changed':
      return {
        tone: 'success',
        title: 'License changed',
        body: [`This facility now uses ${label} ${d.replaced?.to ?? ''}, active through ${formatDate(d.expiry)}.`],
      };
    case 'recorded':
      return {
        tone: 'success',
        title: 'Saved as the license on file',
        body: ['Set its expiration date (and number, if it changed) in Facility Manager.'],
      };
    case 'unreadable':
      return {
        tone: 'warning',
        title: 'We couldn\'t read this document',
        body: [`Upload a clear photo of the ${label} itself, or a file of it (PDF, JPEG or PNG). The whole document must be in view and the number readable.`],
        boardHint,
      };
    case 'not_accepted':
      return {
        tone: 'warning',
        title: 'We need the license itself',
        body: ['This looks like a state board verification record. Please upload a copy of the license certificate or card.'],
      };
    case 'number_mismatch':
      return {
        tone: 'warning',
        title: 'This is a different license number',
        body: [`The document shows ${d.uploaded ?? 'a different number'}, but this facility has ${d.on_file ?? 'another number'} on file.`,
          'If you\'re moving this facility to a new license, choose "Yes, change to the new license".'],
      };
    case 'number_suffix_variant':
      return {
        tone: 'info',
        title: 'This matches your license on file',
        body: [`The document shows ${d.uploaded ?? 'your license number'}. The number on file, ${d.on_file ?? ''}, adds letters at the end to tell your locations apart.`,
          'Submit it for review and our team will confirm it.'],
      };
    case 'change_number_mismatch':
      return {
        tone: 'warning',
        title: 'The number doesn\'t match the document',
        body: [`The document shows ${d.uploaded ?? 'a different number'}, but you entered ${d.expected ?? ''}.`],
      };
    case 'state_mismatch':
      return {
        tone: 'warning',
        title: 'This license is for a different state',
        body: [`The license must be issued in the facility's state${ctx.facilityState ? ` (${ctx.facilityState})` : ''}.`,
          'Upload the license for that state, or add a new facility for the other location.'],
      };
    case 'address_mismatch':
      return {
        tone: 'warning',
        title: 'The address doesn\'t match',
        body: [`The ${joinParts(d.address_fields ?? [])} on the license doesn't match this facility's delivery address.`,
          'Check the facility address, or upload a license that shows the matching address.'],
      };
    case 'manual_review':
      return {
        tone: 'info',
        title: 'We couldn\'t confirm this with state records',
        body: ['Check that the license number is fully visible and try again, or submit it for review.'],
        boardHint,
      };
    case 'check_failed':
      return {
        tone: 'info',
        title: 'We couldn\'t check this just now',
        body: [`This is a problem on our side, not with your document. Please try again in a few minutes, or submit your ${label} for review.`],
      };
    case 'change_review':
      return {
        tone: 'info',
        title: `${TITLE[ctx.licenseType]} changes are reviewed by our team`,
        body: [`Submit the new ${label} for review and we'll update the facility once it's approved.`],
      };
  }
}

export const ACTION_LABEL: Record<LicenseNextAction, string> = {
  upload_different: 'Upload a different file',
  try_again: 'Try again',
  submit_review: 'Submit for review',
  confirm_change: 'Yes, change to the new license',
  use_document_number: 'Use the number on the document',
  edit_address: 'Edit facility address',
  add_facility: 'Add a new facility for this license',
};

const ADDRESS_PART: Record<string, string> = { line1: 'street address', city: 'city', state: 'state', zip: 'ZIP code' };

function joinParts(fields: readonly string[]): string {
  const parts = fields.map(f => ADDRESS_PART[f]).filter(Boolean);
  if (parts.length <= 1) {
    return parts[0] ?? 'address';
  }
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) {
    return 'the expiration date on the license';
  }
  const date = new Date(`${isoDate}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
